/**
 * parseForeignName — split a single-string passport name into First / Middle / Last.
 *
 * Many non-US passports (Malaysian, Indonesian, Indian, many Middle Eastern
 * issuers) print the entire name as one string with no surname/given-name
 * delimiter. Azure DI's prebuilt-idDocument frequently returns:
 *   FirstName = "ADAM ZIKRY BIN LIZAM"  (whole string)
 *   LastName  = "ADAM ZIKRY BIN LIZAM"  (duplicate of the whole string)
 * which lands "ADAM ZIKRY BIN LIZAM" in the Last name slot in v3 — wrong.
 *
 * PCB convention (per legacy CIF for Adam Lizam):
 *   First  = "Adam"
 *   Middle = "Zikry"
 *   Last   = "Bin Lizam"  (the patronymic / surname-equivalent)
 *
 * Heuristics applied here:
 *   1. If the only data is one combined name, split into first / middle / last
 *      using "Bin"/"Binti"/"Bint"/"Ibn"/"Van"/"Von"/"De"/"Del"/"La" as
 *      surname-marker tokens — everything from the marker onward is last name.
 *   2. If no marker, the LAST word is the surname; everything before it is
 *      split into first (1st token) + middle (the rest).
 *   3. If the AI returned distinct First and Last but `last.startsWith(first)`
 *      OR `first === last`, it's the duplicate case — treat as a single
 *      combined string and re-parse.
 *
 * No fancy ML — just a deterministic best-effort. Officer can always edit.
 */

const SURNAME_MARKERS = new Set([
  'BIN',     // Malay / Indonesian / Arabic — "son of"
  'BINTI',   // Malay / Indonesian — "daughter of"
  'BINT',    // Arabic — "daughter of"
  'IBN',     // Arabic — "son of"
  'AL',      // Arabic definite article (often part of family names)
  'VAN',     // Dutch — "of"
  'VON',     // German — "of"
  'DE',      // Spanish/Portuguese/French — "of"
  'DEL',     // Spanish — "of the"
  'DA',      // Portuguese — "of"
  'DOS',     // Portuguese — "of the"
  'LA',      // Italian/French — "the"
  'LE',      // French — "the"
  'ST',      // "Saint"
  'MC',      // Scottish/Irish (often glued — handled by suffix pattern)
  'MAC',     // Scottish/Irish (often glued)
  "O'",      // Irish — handled if joined like O'BRIEN
]);

export interface ParsedName {
  first: string;
  middle: string;
  last: string;
}

function tokens(input: string): string[] {
  return input
    .replace(/,\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter((t) => t.length > 0);
}

function fromTokens(parts: string[]): ParsedName {
  if (parts.length === 0) return { first: '', middle: '', last: '' };
  if (parts.length === 1) return { first: parts[0]!, middle: '', last: '' };
  // Find surname marker — everything from there onward is last name.
  const upperParts = parts.map((p) => p.toUpperCase());
  const markerIdx = upperParts.findIndex((p) => SURNAME_MARKERS.has(p));
  if (markerIdx > 0 && markerIdx < parts.length - 1) {
    const last = parts.slice(markerIdx).join(' ');
    const givens = parts.slice(0, markerIdx);
    if (givens.length === 1) return { first: givens[0]!, middle: '', last };
    return { first: givens[0]!, middle: givens.slice(1).join(' '), last };
  }
  // No marker — assume LAST word is surname.
  const last = parts[parts.length - 1]!;
  const givens = parts.slice(0, parts.length - 1);
  if (givens.length === 1) return { first: givens[0]!, middle: '', last };
  return { first: givens[0]!, middle: givens.slice(1).join(' '), last };
}

/**
 * Parse a foreign-passport name. Accepts any of:
 *   - One combined string ("ADAM ZIKRY BIN LIZAM")
 *   - { first, last } where last duplicates first or contains the full string
 *   - Already-clean { first, middle, last } — returns as-is
 *
 * Returns titlecase strings. Caller decides how to merge with officer-edited
 * values (the buildPatchFromNra empty-only guard handles that).
 */
export function parseForeignName(input: {
  fullName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): ParsedName {
  const fn = (input.firstName ?? '').trim();
  const mn = (input.middleName ?? '').trim();
  const ln = (input.lastName ?? '').trim();
  const full = (input.fullName ?? '').trim();

  // Case A — clean distinct first/last that don't look duplicated.
  const looksDuplicated =
    fn && ln && (fn === ln || ln.startsWith(fn + ' ') || fn.startsWith(ln + ' '));
  if (fn && ln && !looksDuplicated && tokens(fn).length === 1 && tokens(ln).length <= 3) {
    return {
      first: titleCase(fn),
      middle: titleCase(mn),
      last: titleCase(ln),
    };
  }

  // Case B — pick the most-complete source string and re-parse.
  const candidates = [full, fn, ln].filter((s) => s.length > 0);
  // Longest token-count wins (most complete name).
  candidates.sort((a, b) => tokens(b).length - tokens(a).length);
  const best = candidates[0] ?? '';
  const parts = tokens(best);
  const parsed = fromTokens(parts);
  return {
    first: titleCase(parsed.first),
    middle: titleCase(parsed.middle),
    last: titleCase(parsed.last),
  };
}

/** Convert ALL CAPS or lower to Title Case while keeping multi-token strings. */
function titleCase(s: string): string {
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((w) => {
      // Preserve apostrophes ("O'BRIEN" → "O'Brien"), hyphens ("J-1" → "J-1").
      if (w.length === 0) return w;
      // All-caps single letter (middle initial) — keep upper.
      if (w.length === 1) return w.toUpperCase();
      const first = w.charAt(0).toUpperCase();
      const rest = w.slice(1).toLowerCase();
      return first + rest;
    })
    .join(' ');
}
